import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import {
  useDialog,
  DialogAdornment,
  DialogWindow,
  DialogHeader,
  DialogBody,
  H4,
  fontSize,
  color,
  fontWeight,
  lineHeight,
  device,
  space,
} from '@ticketswap/solar'
import { CloseAlt } from '@ticketswap/comets'

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: ${props => props.withBorder && space[16]};
  border-top: ${props =>
    props.withBorder && `1px solid ${color.spaceLightest}`};

  & + ${() => Row} {
    margin-top: ${space[16]};
  }
`

const SmallPrint = styled.p`
  font-size: ${fontSize[16]};
  color: ${color.spaceLight};
  margin: 0;
`

const Price = styled.span`
  font-weight: ${props => props.total && fontWeight.semiBold};
  font-size: ${fontSize[18]};
  line-height: ${lineHeight.title};
  padding-left: ${space[32]};

  @media ${device.tablet} {
    font-size: ${fontSize[20]};
  }
`

const SemiBoldH4 = styled(H4)`
  font-weight: ${fontWeight.semiBold};
`

function PriceInformationDialog({
  children,
  t,
  ticketQuantity,
  ticketDescription,
  priceWithoutFees,
  serviceFee,
  transactionFee,
  totalPrice,
  type,
}) {
  const { show, hide, getWindowProps } = useDialog()
  const explanation = {
    lottery: {
      transactionFee: t('common:priceInformationDialog.transactionFeeLottery'),
    },
    ticket: {
      price: t('common:priceInformationDialog.priceSmall'),
      serviceFee: t('common:priceInformationDialog.serviceFeeSmall'),
      transactionFee: t('common:priceInformationDialog.transactionFeeSmall'),
    },
  }[type]

  return (
    <>
      {children({ show })}
      <DialogWindow {...getWindowProps()}>
        <DialogHeader>
          {t('common:priceInformationDialog.title')}
          <DialogAdornment right>
            <button onClick={hide}>
              <CloseAlt size={24} />
            </button>
          </DialogAdornment>
        </DialogHeader>
        <DialogBody>
          <Row>
            <span>
              <H4>{`${ticketQuantity} × ${ticketDescription}`}</H4>
              {Boolean(explanation.price) && (
                <SmallPrint>{explanation.price}</SmallPrint>
              )}
            </span>
            <Price>{priceWithoutFees}</Price>
          </Row>

          {serviceFee && (
            <Row withBorder>
              <span>
                <H4>{t('common:priceInformationDialog.serviceFeeTitle')}</H4>
                {Boolean(explanation.serviceFee) && (
                  <SmallPrint>{explanation.serviceFee}</SmallPrint>
                )}
              </span>
              <Price>{serviceFee}</Price>
            </Row>
          )}

          <Row withBorder={!serviceFee}>
            <span>
              <H4>{t('common:priceInformationDialog.transactionFeeTitle')}</H4>
              {Boolean(explanation.transactionFee) && (
                <SmallPrint>{explanation.transactionFee}</SmallPrint>
              )}
            </span>
            <Price>{transactionFee}</Price>
          </Row>

          <Row withBorder>
            <SemiBoldH4>{t('common:priceInformationDialog.total')}</SemiBoldH4>
            <Price total>{totalPrice}</Price>
          </Row>
        </DialogBody>
      </DialogWindow>
    </>
  )
}

PriceInformationDialog.defaultProps = {
  type: 'ticket',
}

PriceInformationDialog.propTypes = {
  t: PropTypes.func.isRequired,
  ticketQuantity: PropTypes.number.isRequired,
  priceWithoutFees: PropTypes.string.isRequired,
  serviceFee: PropTypes.string,
  transactionFee: PropTypes.string.isRequired,
  totalPrice: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['lottery', 'ticket']).isRequired,
}

export default PriceInformationDialog
